import BookmarkIconFilled from 'assets/img/bookmark-icon-filled.svg'
import BookmarkIconUnfilled from 'assets/img/bookmark-icon-unfilled.svg'
import {
  QuestionPill,
  QuestionTopRow,
  HeaderContainer,
  QuestionContainer,
  AssessmentHeaderRightSection,
  WorkspaceTitle
} from '../styles'
import { MouseEvent, useContext } from 'react'
import Cookies from 'js-cookie'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import { getAvatarTextColor } from 'components/ClassroomHeaderBar/ClassroomHeaderBar'
import { UserBadge } from 'components/ClassroomHeaderBar/styles'
import { useAppSelector } from 'app/hooks'
import { selectUser } from 'app/reducer/authSlice'
import { getStudentInitials } from 'util/student'
import { selectCurrentClassroom } from 'app/reducer/studentSlice'
import { AppCookies } from 'util/constants'
import SpeechControlsV2 from 'components/Assessment/SpeechControlsV2'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import {
  AssignmentBodyItem,
  AssignmentPage,
  QuestionBodyItem
} from 'interfaces/assignments'
import { isQuestionSetPage } from '../utils'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import { ViewMode } from 'interfaces/app'
import ScrollingText from 'components/common/ScrollingText'

type AssignmentQuestionBodyItem = QuestionBodyItem

interface HeaderProps {
  assessmentName: string
  assessmentType?: string | undefined
  currentPage?: number | string
  studentPages?: number | string
  onQuestionSelector: () => void
  onToggleAssignmentBookmark?: (
    nextBookmarkedState: boolean
  ) => Promise<void> | void
}

const Header = ({
  assessmentName,
  assessmentType,
  currentPage: currentPageLabel,
  studentPages,
  onQuestionSelector,
  onToggleAssignmentBookmark
}: HeaderProps) => {
  const { questions, questionIdx, onChange, viewMode } =
    useContext(QuestionnaireContext)
  const user = useAppSelector(selectUser)
  const studentInitials = getStudentInitials(user)
  const currentClassroom = useAppSelector(selectCurrentClassroom)
  /**
   * Keep the assessment badge aligned with the classroom header by resolving
   * the same selected background/text color pair from auth state and cookies.
   */
  const selectedColorId =
    user?.selected_color?.color_code_id ||
    user?.selected_color?.fk_color_code_id
  const authSelectedColorCode = (user?.available_color_codes || []).find(
    (colorCode) => colorCode.id === selectedColorId
  )?.code
  const selectedColorCode =
    user?.avatar_background_color ||
    authSelectedColorCode ||
    Cookies.get(AppCookies.profile_color_code)
  const selectedAvatarTextColor =
    user?.avatar_text_color || getAvatarTextColor(selectedColorCode)
  const {
    text_to_speech_enabled,
    assignment,
    currentPage: currentAssignmentPage
  } = useAppSelector(SelectAssignmentState)

  /**
   * Assessment headers need both the current position and the unanswered count.
   * The index comes from the questionnaire context, while the remaining count
   * stays based on unanswered questions so the subtitle reflects work left.
   */
  const currentQuestionNumber = questionIdx + 1
  const totalQuestions = questions.length
  const answeredQuestions = questions.filter((q) => q.answer !== null).length
  const defaultQuestionsRemaining = questions.length - answeredQuestions
  /**
   * Benchmark review keeps question navigation active, but the remaining count
   * should disappear because the student is reviewing a submitted attempt.
   */
  const isReadOnlyViewMode = viewMode === ViewMode.Student
  const isAssignment = assessmentType === 'assignments'

  const allAssignmentQuestionIds = (assignment?.pages || [])
    .filter((page: AssignmentPage) => isQuestionSetPage(page))
    .flatMap((page: AssignmentPage) =>
      (page.body_items as Array<AssignmentBodyItem | QuestionBodyItem>)
        .filter(
          (
            item: AssignmentBodyItem | QuestionBodyItem
          ): item is AssignmentQuestionBodyItem =>
            item.body_item_type === 'question' && 'answer' in item
        )
        .map((item: AssignmentQuestionBodyItem) => Number(item.id))
    )

  const uniqueAssignmentQuestionIds = Array.from(
    new Set<number>(allAssignmentQuestionIds)
  )

  const completedAssignmentQuestionCount = (assignment?.pages || [])
    .filter((page: AssignmentPage) => isQuestionSetPage(page))
    .flatMap((page: AssignmentPage) => page.body_items)
    .filter(
      (
        item: AssignmentBodyItem | QuestionBodyItem
      ): item is AssignmentQuestionBodyItem =>
        item.body_item_type === 'question' &&
        'answer' in item &&
        Number.isInteger(item.answer)
    ).length

  const assignmentQuestionsRemaining = Math.max(
    uniqueAssignmentQuestionIds.length - completedAssignmentQuestionCount,
    0
  )

  const questionsRemaining =
    assessmentType === 'assignments'
      ? assignmentQuestionsRemaining
      : defaultQuestionsRemaining
  const isAssignmentBookmark = Boolean(currentAssignmentPage?.is_bookmarked)
  const isBenchmarkOrFormativeBookmark = Boolean(
    questions[questionIdx]?.bookmark
  )
  const isBookmarked =
    assessmentType === 'assignments'
      ? isAssignmentBookmark
      : isBenchmarkOrFormativeBookmark

  const bookmarkQuestion = async (event: MouseEvent<HTMLImageElement>) => {
    event.stopPropagation()

    if (assessmentType === 'assignments') {
      const nextBookmarkedState = !isAssignmentBookmark

      await onToggleAssignmentBookmark?.(nextBookmarkedState)

      return
    }

    if (!questions[questionIdx]) return
    onChange('bookmark', !isBenchmarkOrFormativeBookmark)
  }

  const isTTSEnabled = isAssignment
    ? text_to_speech_enabled
    : Cookies.get('text_to_speech_enabled') === 'true'

  return (
    <HeaderContainer>
      <QuestionContainer>
        <QuestionPill
          type='button'
          aria-label='Open formative question details'
          onClick={onQuestionSelector}
        >
          <div className='flex' style={{ gap: 10, minWidth: 0 }}>
            <img
              src={isBookmarked ? BookmarkIconFilled : BookmarkIconUnfilled}
              alt='bookmark'
              onClick={bookmarkQuestion}
              style={{ cursor: 'pointer' }}
            />
            <QuestionTopRow>
              {/**
               * Reuse the shared scrolling text behavior so long assessment names
               * have the same overflow and hover experience as the teacher portal.
               */}
              <ScrollingText
                tag='p'
                title={assessmentName}
                containerStyles={{ minWidth: 0 }}
                elementStyles={{
                  fontSize: '15px',
                  lineHeight: '17px',
                  margin: 0,
                  paddingTop: '2px'
                }}
              >
                {assessmentName}
              </ScrollingText>
              <WorkspaceTitle>
                {assessmentType === 'assignments' ? (
                  <AssignmentHeaderTitle
                    assessmentType={assessmentType}
                    currentPage={currentPageLabel}
                    studentPages={studentPages}
                    questionsRemaining={questionsRemaining}
                  />
                ) : (
                  <AssessmentHeaderTitle
                    currentQuestionNumber={currentQuestionNumber}
                    totalQuestions={totalQuestions}
                    questionsRemaining={questionsRemaining}
                    hideQuestionsRemaining={isReadOnlyViewMode}
                  />
                )}
              </WorkspaceTitle>
            </QuestionTopRow>
          </div>
          <ChevronIcon direction='down' ariaLabel='Open page navigation' />
        </QuestionPill>
      </QuestionContainer>

      <AssessmentHeaderRightSection>
        {isTTSEnabled && <SpeechControlsV2 />}
        <UserBadge
          $backgroundColor={selectedColorCode}
          $textColor={selectedAvatarTextColor}
        >
          {studentInitials}
        </UserBadge>
      </AssessmentHeaderRightSection>
    </HeaderContainer>
  )
}

const AssignmentHeaderTitle = ({
  assessmentType,
  questionsRemaining,
  currentPage,
  studentPages
}: {
  questionsRemaining?: number
  assessmentType?: string
  currentPage?: number | string
  studentPages?: number | string
}) => {
  return (
    <>
      {assessmentType === 'assignments' && (
        <>
          {currentPage && studentPages ? (
            <>
              Page {currentPage} of {studentPages}{' '}
              <span style={{ color: '#B0B0B0' }}>•</span>{' '}
              {questionsRemaining ?? 0} Questions Remaining
            </>
          ) : null}
        </>
      )}
    </>
  )
}
const AssessmentHeaderTitle = ({
  currentQuestionNumber,
  totalQuestions,
  questionsRemaining,
  hideQuestionsRemaining = false
}: {
  currentQuestionNumber: number
  totalQuestions: number
  questionsRemaining: number
  hideQuestionsRemaining?: boolean
}) => {
  return (
    <>{`Question ${currentQuestionNumber} of ${totalQuestions}${
      hideQuestionsRemaining ? '' : ` (${questionsRemaining} Remaining)`
    }`}</>
  )
}

export default Header
